/**
 * Per-page <title> and meta description for the single-page docs site.
 *
 * index.html carries one static title for every route, so without this each
 * docs page reaches search engines with the same title and no description.
 * The prerender also hands both to link previews (og:title, og:description).
 */

import { createContext, useContext, useEffect } from 'react';

const SITE_NAME = 'Sokuji';

/** index.html's own <title>, which pages without one show. */
export const SITE_TITLE = 'Sokuji - Real-time AI Translation';
const MAX_DESCRIPTION = 160;
const ENTITIES: Record<string, string> = { '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };

export function documentTitle(pageTitle: string): string {
  return `${pageTitle} | ${SITE_NAME}`;
}

/**
 * Turns a page's overview HTML into a meta description: plain text, cut after
 * the first sentence, or after the second when the first is short, and never
 * longer than a search result shows.
 */
export function metaDescription(html: string): string {
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&(nbsp|amp|lt|gt|quot|#39);/g, (e) => ENTITIES[e])
    .replace(/\s+/g, ' ')
    .trim();

  // Sentence ends: Latin punctuation followed by a space, or a CJK/Arabic full stop.
  const ends = [...text.matchAll(/[.!?](?=\s)|[。！？؟]/g)].map((m) => m.index! + 1);
  const first = ends[0] ?? text.length;
  const second = ends[1] ?? text.length;
  let out = text.slice(0, first < 20 && second <= MAX_DESCRIPTION ? second : first).trim();

  if (out.length > MAX_DESCRIPTION) {
    const cut = out.slice(0, MAX_DESCRIPTION - 1);
    const space = cut.lastIndexOf(' ');
    out = (space > MAX_DESCRIPTION / 2 ? cut.slice(0, space) : cut).trimEnd() + '…';
  }
  return out;
}

/** What a page asked for, collected while prerendering (effects never run there). */
export interface HeadData {
  title?: string;
  description?: string;
}

/** Provided only by the prerender entry; in the browser the effect below does the work. */
export const HeadContext = createContext<HeadData | null>(null);

/**
 * Sets the meta description while the calling page is mounted, and leaves the
 * title at index.html's (the home page keeps it).
 */
export function useMetaDescription(overviewHtml: string): void {
  const head = useContext(HeadContext);
  if (head) head.description = metaDescription(overviewHtml);

  useEffect(() => {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = metaDescription(overviewHtml);

    // Removed, not restored: after a client-side navigation what was there
    // before can be the description the prerender wrote for the page first
    // loaded, which no longer applies.
    return () => meta.remove();
  }, [overviewHtml]);
}

/** Sets the document title and meta description while the calling page is mounted. */
export function useDocumentMeta(pageTitle: string, overviewHtml: string): void {
  const head = useContext(HeadContext);
  if (head) head.title = documentTitle(pageTitle);
  useMetaDescription(overviewHtml);

  useEffect(() => {
    document.title = documentTitle(pageTitle);
    // Back to the site default for the same reason the description goes.
    return () => {
      document.title = SITE_TITLE;
    };
  }, [pageTitle]);
}
